import {useSlots} from "vue";
import { Notify, copyToClipboard } from 'quasar'
import { sub, endOfDay, startOfDay, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear } from 'date-fns'
import { getAuth, signOut } from "firebase/auth"
import { RouterInstance } from '../router'
import { resetStores } from '../stores/index'

/**
 *
 * @param {'7days'|'30days'|'pm'|'pw'|'mtd'|'ytd'|'bctd'|'pbc'|'custom'} timerangeCode
 * @param {number} billingCycleDay - day of the month that billing is calculated. If a given month is shorter than this number, the billing day will be the last day of that month
 * @returns {Object<from:Date, to:Date>}
 */
export function getTimerange(timerangeCode) {
  const now = new Date()

  if (timerangeCode === '7days') { // last 7 days, including today
    return {
      from: startOfDay(sub(now, {days:6})),
      to: endOfDay(now)
    }
  } else if (timerangeCode === '30days') { // last 30 days, including today
    return {
      from: startOfDay(sub(now, {days:29})),
      to: endOfDay(now)
    }
  } else if (timerangeCode === 'pm') { // past month
    return {
      from: startOfMonth(sub(now, {months:1})),
      to: endOfMonth(sub(now, {months:1}))
    }
  } else if (timerangeCode === 'pw') { // past week
    return {
      from: startOfWeek(sub(now, {weeks:1})),
      to: endOfWeek(sub(now, {weeks:1}))
    }
  } else if (timerangeCode === 'mtd') { // month to date
    return {
      from: startOfMonth(now),
      to: endOfDay(now)
    }
  } else if (timerangeCode === 'ytd') { // year to date
    return {
      from: startOfYear(now),
      to: endOfDay(now)
    }
  } else if (timerangeCode === 'bctd') { // billing cycle to date
    return {
      from: startOfMonth(sub(now, {months:1})),
      to: endOfMonth(sub(now, {months:1}))
    }
  } else if (timerangeCode === 'pbc') { // previous billing cycle
    return {
      from: startOfMonth(sub(now, {months:1})),
      to: endOfMonth(sub(now, {months:1}))
    }
  }
}

export function humanFileSize (bytes, si = false, dp = 1) {
  const thresh = si ? 1000 : 1024
  if (Math.abs(bytes) < thresh) {
    return bytes + ' B'
  }
  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp
  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)
  return bytes.toFixed(dp) + ' ' + units[u]
}

export function copy (text, message = 'Copied', timeout = 1500) {
  copyToClipboard(text)
    .then(() => {
      Notify.create({
        message: message,
        position: 'top',
        timeout: timeout,
        type: 'positive'
      })
    })
}

export async function signout (){
  const auth = getAuth()
  await signOut(auth)
  await RouterInstance.push('/auth/login')
  await resetStores()
}

export function multilineOrCommaToArray(str) {
  return str.split(/[\s,]+/).map(x => x.trim()).filter(x => x !== '')
}

export function slotHasContent(slotName) {
  const slots = useSlots()
  return Boolean(!!slots[slotName] && slots[slotName]()[0].children.length > 0);
}

export function notify(message, {type = 'positive', timeout = 3000, position = 'top', actions}={}) {
  Notify.create({
    message,
    position,
    timeout,
    type,
    actions
  })
}

export const roles = {
  data: {
    NONE: { n:0, label: 'None', value: 'NONE', description: 'Cannot view submissions' },
    VIEW: { n:10, label: 'View', value: 'VIEW', description: 'Can view submissions, but not manage them' },
    MANAGE: { n:20, label: 'Manage', value: 'MANAGE', description: 'Can view and manage submissions' }
  },
  forms: {
    NONE: { n:0, label: 'List', value: 'NONE', description: 'Can only view list of forms, but not their details' },
    // VIEW: { n:10, label: 'View', value: 'VIEW', description: 'Can view forms details, but not edit them' },
    MANAGE: { n:20, label: 'Manage', value: 'MANAGE', description: 'Can create, edit and delete forms' }
  },
  team: {
    // NONE: { n:0, label: 'None', value: 'NONE', description: 'Cannot view or manage team' },
    VIEW: { n:10, label: 'View', value: 'VIEW', description: 'View other team members and their roles' },
    INVITE: { n:20, label: 'Invite', value: 'INVITE', description: 'Can invite other team members' },
    MANAGE: { n:30, label: 'Manage', value: 'MANAGE', description: 'Can invite, change roles and remove other team members' }
  },
  workspace: {
    VIEW: { n:0, label: 'View', value: 'VIEW', description: 'Can view workspace settings' },
    MANAGE: { n:20, label: 'Manage', value: 'MANAGE', description: 'Can manage workspace settings' },
    ADMIN: { n:30, label: 'Admin', value: 'ADMIN', description: 'Manage workspace settings and delete it' },
  }
}

/**
 * resolves to the result of cond(), once it is truthy. Resolve to false if timeout millis have passed
 * @param {Function} cond
 * @param {number} timeout - number of millis before resolving to false. Default is 5000
 * @param {number} checkInterval - number of millis to wait before checking cond() again. default is 50
 * @returns {Promise<boolean>}
 */
export async function waitUntil(cond, timeout = 5000, checkInterval = 50) {
  const startTime = Date.now();

  return new Promise(resolve => {
    const checkCondition = () => {
      let res = cond();
      if (res) {
        resolve(res);
      } else if (Date.now() - startTime >= timeout) {
        resolve(false)
      } else {
        setTimeout(checkCondition, checkInterval); // Check again after a short delay
      }
    };

    checkCondition();
  });
}
